import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BusinessCenterOSAdminShell } from "../../components/layout/BusinessCenterOSAdminShell";
import { getSession } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { loadARRecoveryWorkspace } from "../../lib/arRecoveryData";
import { loadBusinessCenterCompanyUsers, refreshBusinessCenterCompanyUsers } from "./businessCenterCompanyUsersData";
import type { BusinessCenterUserRecord } from "../../lib/types";

interface SessionUser {
  email?: string;
  name?: string | null;
  role?: string;
}

export function InternalUserWorkspacePage({ profileOnly = false }: { profileOnly?: boolean }) {
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);
  const [users, setUsers] = useState<BusinessCenterUserRecord[]>(() => loadBusinessCenterCompanyUsers());
  const workspace = useMemo(() => loadARRecoveryWorkspace(), []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getSession(), refreshBusinessCenterCompanyUsers()])
      .then(([session, refreshedUsers]) => {
        if (cancelled) return;
        setSessionUser(session.user);
        setUsers(refreshedUsers);
      })
      .catch(() => {
        if (!cancelled) setSessionUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const profile = useMemo(
    () => resolveInternalUserProfile(users, sessionUser),
    [sessionUser, users],
  );
  const collectorNames = useMemo(
    () => new Set([profile?.displayName, profile?.fullName].filter((name): name is string => Boolean(name))),
    [profile?.displayName, profile?.fullName],
  );
  const assignedAccounts = useMemo(
    () => profile
      ? workspace.accounts.filter((account) => collectorNames.has(account.assignedCollector)).slice(0, 6)
      : [],
    [collectorNames, profile, workspace.accounts],
  );
  const assignedAccountIds = useMemo(
    () => new Set(assignedAccounts.map((account) => account.id)),
    [assignedAccounts],
  );
  const duePromises = useMemo(
    () => profile
      ? workspace.promises
          .filter((promise) => collectorNames.has(promise.collector) && (promise.status === "Open" || promise.status === "Due Today"))
          .slice(0, 5)
      : [],
    [collectorNames, profile, workspace.promises],
  );
  const dueFollowUps = useMemo(
    () => profile
      ? workspace.ticklers
          .filter((tickler) => collectorNames.has(tickler.assignedCollector) && tickler.status !== "Completed" && tickler.status !== "Cancelled")
          .slice(0, 5)
      : [],
    [collectorNames, profile, workspace.ticklers],
  );
  const recentActivity = useMemo(
    () => profile
      ? workspace.recoveryActivityEvents.filter((event) => typeof event.accountId === "string" && assignedAccountIds.has(event.accountId)).slice(0, 5)
      : [],
    [assignedAccountIds, profile, workspace.recoveryActivityEvents],
  );

  return (
    <BusinessCenterOSAdminShell
      title={profileOnly ? "My Profile" : "My Workspace"}
      description={profileOnly
        ? "Review your internal CollabRecover AR Solutions profile, role, team, and notification preferences."
        : "Your internal operating surface for assigned accounts, open work, promises, follow-ups, and recent recovery activity."}
      eyebrow="Truevance Connect Internal Platform"
      statusItems={[
        { label: "Role", value: roleLabel(profile?.role || sessionUser?.role || "viewer") },
        { label: "Assigned Accounts", value: assignedAccounts.length },
        { label: "Open Follow-Ups", value: dueFollowUps.length },
      ]}
      actions={
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/ar-recovery/customer-accounts" className="rounded-xl border border-cyan-300/20 bg-cyan-300/10 px-4 py-3 text-sm font-black text-cyan-100">
            Customer Accounts
          </Link>
          <Link to={profileOnly ? "/admin/my-workspace" : "/admin/my-profile"} className="rounded-xl border border-white/10 bg-white/[.06] px-4 py-3 text-sm font-black text-white">
            {profileOnly ? "My Workspace" : "My Profile"}
          </Link>
        </div>
      }
    >
      <section className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-5 xl:grid-cols-[.8fr_1.2fr]">
          <article className="rounded-3xl border border-white/10 bg-white/[.06] p-5">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-100">Signed-In User</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-white">
              {profile?.displayName || profile?.fullName || sessionUser?.name || sessionUser?.email || "Internal User"}
            </h2>
            <div className="mt-4 grid gap-3 text-sm font-semibold text-slate-200">
              <ProfileRow label="Email" value={profile?.email || sessionUser?.email || "Not Captured"} />
              <ProfileRow label="Role" value={roleLabel(profile?.role || sessionUser?.role || "viewer")} />
              <ProfileRow label="Job Title" value={profile?.jobTitle || "Not Captured"} />
              <ProfileRow label="Department" value={profile?.department || "Not Captured"} />
              <ProfileRow label="Team" value={profile?.team || "Not Captured"} />
              <ProfileRow label="Phone" value={profile?.phone || "Not Captured"} />
              <ProfileRow label="Notifications" value={profile?.notificationPreference ? labelize(profile.notificationPreference) : "Not Captured"} />
            </div>
          </article>

          <article className="rounded-3xl border border-white/10 bg-white/[.06] p-5">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-100">Today</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-white">Operational Focus</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <Metric label="Assigned Accounts" value={assignedAccounts.length} />
              <Metric label="Promises Due" value={duePromises.length} />
              <Metric label="Follow-Ups Due" value={dueFollowUps.length} />
            </div>
            <div className="mt-5 grid gap-3 lg:grid-cols-2">
              <WorkList title="Assigned Accounts" empty="No assigned accounts found for this profile." items={assignedAccounts.map((account) => ({
                title: account.customerName,
                detail: `${account.customerNumber} | ${account.agingSummary} | ${account.recoveryStatus}`,
              }))} />
              <WorkList title="Promises Due" empty="No open promises are due." items={duePromises.map((promise) => ({
                title: promise.customerName,
                detail: `${promise.status} | ${formatDate(promise.promiseDate)} | ${promise.collector}`,
              }))} />
              <WorkList title="Follow-Ups" empty="No follow-ups are currently due." items={dueFollowUps.map((tickler) => ({
                title: tickler.customerName,
                detail: `${tickler.ticklerType} | ${formatDate(tickler.dueDate)} | ${tickler.assignedCollector}`,
              }))} />
              <WorkList title="Recent Activity" empty="No recent activity found." items={recentActivity.map((event) => ({
                title: event.eventType,
                detail: `${event.customerName || "Account"} | ${formatDate(event.createdAt)} | ${event.actorName || "System"}`,
              }))} />
            </div>
          </article>
        </div>
      </section>
    </BusinessCenterOSAdminShell>
  );
}

function resolveInternalUserProfile(users: BusinessCenterUserRecord[], sessionUser: SessionUser | null) {
  const email = sessionUser?.email?.toLowerCase() || "";
  if (!email) return null;
  return users.find((user) => user.email.toLowerCase() === email) || null;
}

function roleLabel(role: string) {
  if (role === "company_admin" || role === "admin" || role === "kreatethedate_admin") return "Administrator";
  if (role === "company_manager" || role === "manager") return "Manager";
  if (role === "company_supervisor" || role === "supervisor") return "Supervisor";
  if (role === "company_team_member" || role === "team_member") return "Collector";
  if (role === "customer_client_user" || role === "client") return "Client User";
  return "Read Only";
}

function labelize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-white/10 bg-slate-950/30 px-3 py-2">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">{label}</span>
      <span className="text-right text-sm font-black text-white">{value}</span>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-cyan-300/15 bg-cyan-300/10 p-4">
      <p className="text-xs font-black uppercase tracking-[0.14em] text-cyan-100">{label}</p>
      <p className="mt-2 text-3xl font-bold text-white">{value}</p>
    </div>
  );
}

function WorkList({ title, empty, items }: { title: string; empty: string; items: Array<{ title: string; detail: string }> }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/30 p-3">
      <p className="text-xs font-black uppercase tracking-[0.14em] text-cyan-100">{title}</p>
      <div className="mt-3 grid gap-2">
        {items.length ? items.map((item) => (
          <div key={`${item.title}-${item.detail}`} className="rounded-xl border border-white/10 bg-white/[.04] p-3">
            <p className="text-sm font-black text-white">{item.title}</p>
            <p className="mt-1 text-xs font-semibold leading-5 text-slate-300">{item.detail}</p>
          </div>
        )) : (
          <p className="rounded-xl border border-dashed border-white/10 p-3 text-sm font-semibold text-slate-300">{empty}</p>
        )}
      </div>
    </div>
  );
}
